<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <h2><i class="fas fa-user-edit"></i> {{ $t('editProfile') }}</h2>

      <!-- Mensagens de erro do backend -->
      <div v-if="backendError.length" class="error-message-block">
        <div v-for="(msg, i) in backendError" :key="i" class="error-text">
          <i class="fas fa-exclamation-circle"></i> {{ msg }}
        </div>
      </div>

      <form @submit.prevent="submitPerfil">
        <div class="form-grid">

          <!-- Telefone -->
          <div class="form-group full">
            <label>{{ $t('phone') }}</label>
            <div class="phone-input-wrapper">
              <span class="prefix">+55</span>
              <input v-model="perfil.telefone" v-mask="'(##) #####-####'" type="text" placeholder="(45) 99958-2744"
                required />
            </div>
          </div>

          <!-- Tipo de Documento -->
          <div class="form-group">
            <label>{{ $t('documentType') }}</label>
            <select v-model="perfil.documento_tipo">
              <option v-for="option in documentoTipos" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>{{ $t('document') }}</label>
            <input v-model="perfil.documento" type="text" required />
          </div>

          <!-- Aviso -->
          <div class="alert-info full">
            <i class="fas fa-info-circle"></i>
            {{ $t('photoDocumentNotice') }}
          </div>
          

          <!-- Upload Frente -->
          <div class="form-group full">
            <label>{{ $t('registration.frontImage') }}</label>
            <input type="file" @change="onFileChange('frente', $event)" accept=".jpg,.jpeg,.png,.pdf" />
            <div v-if="frentePreview" class="preview-container">
              <img v-if="isImage(frente)" :src="frentePreview" alt="Frente" />
              <span v-else>{{ frente?.name }}</span>
            </div>
          </div>

          <!-- Upload Verso -->
          <div class="form-group full">
            <label>{{ $t('registration.backImage') }}</label>
            <input type="file" @change="onFileChange('verso', $event)" accept=".jpg,.jpeg,.png,.pdf" />
            <div v-if="versoPreview" class="preview-container">
              <img v-if="isImage(verso)" :src="versoPreview" alt="Verso" />
              <span v-else>{{ verso?.name }}</span>
            </div>
          </div>

        </div>

        <div class="modal-actions">
          <button type="button" class="cancel-btn" @click="close">{{ $t('cancel') }}</button>
          <button type="submit" class="save-btn">{{ $t('save') }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mask } from 'vue-the-mask';
import { mapGetters } from 'vuex';
import { extractMessagesFromResponse } from '@/components/helpers/responseHelper';

export default {
  name: 'AlterarPerfilModal',
  directives: { mask },
  computed: {
    ...mapGetters('auth', ['getUser']),
    isBrazilian() {
      return this.getUser?.estrangeiro === true || this.getUser?.estrangeiro === '1';
    },
    documentoTipos() {
      return this.isBrazilian ? ['CPF'] : ['RUC', 'Cédula'];
    }
  },
  data() {
    return {
      perfil: {
        telefone: '',
        documento: '',
        documento_tipo: ''
      },
      frente: null,
      verso: null,
      frentePreview: null,
      versoPreview: null,
      backendError: []
    };
  },
  mounted() {
    if (this.getUser) {
      const telefone = this.getUser.telefone || '';
      this.perfil.telefone = telefone.replace('+55 ', '');
      this.perfil.documento = this.getUser.documento || '';
      this.perfil.documento_tipo = this.documentoTipos[0];
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    isImage(file) {
      return file && file.type?.startsWith('image/');
    },
    onFileChange(campo, event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        this.backendError = [this.$t('fileTypeError') || 'Tipo de arquivo inválido.'];
        return;
      }

      if (file.size > maxSize) {
        this.backendError = [this.$t('fileSizeError') || 'Arquivo excede o tamanho permitido.'];
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (campo === 'frente') {
          this.frente = file;
          this.frentePreview = this.isImage(file) ? reader.result : null;
        } else if (campo === 'verso') {
          this.verso = file;
          this.versoPreview = this.isImage(file) ? reader.result : null;
        }
      };

      if (this.isImage(file)) {
        reader.readAsDataURL(file);
      } else {
        if (campo === 'frente') {
          this.frente = file;
          this.frentePreview = null;
        } else {
          this.verso = file;
          this.versoPreview = null;
        }
      }
    },
    async submitPerfil() {
      this.backendError = [];

      try {
        const formData = new FormData();
        formData.append('telefone', '+55 ' + this.perfil.telefone);
        formData.append('documento', this.perfil.documento);
        formData.append('documento_tipo', this.perfil.documento_tipo);
        if (this.frente) formData.append('frente', this.frente);
        if (this.verso) formData.append('verso', this.verso);

        const token = localStorage.getItem('token');
        if (!token) {
          this.backendError.push(this.$t('authTokenMissing') || 'Token de autenticação ausente.');
          return;
        }

        await axios.post(
          `${process.env.VUE_APP_API_URI}/ecom/cliente/update-profile`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        this.$emit('salvar');
        this.close();
        window.location.reload();
      } catch (error) {
        console.error('Erro ao salvar perfil:', error.response?.data || error.message);
        this.backendError = extractMessagesFromResponse(error, this.$t('errorMessage'));
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(44, 62, 80, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  width: 95%;
  max-width: 650px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.error-message-block {
  background-color: #fdecea;
  border: 1px solid #e74c3c;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
}

.error-text {
  color: #e74c3c;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

h2 {
  margin-bottom: 1.5rem;
  font-size: 1.6rem;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-message {
  background: #fdecea;
  color: #c0392b;
  border: 1px solid #e0b4b4;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.full {
  grid-column: span 2;
}

input,
select {
  padding: 0.6rem 0.8rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
}

.preview-container {
  margin-top: 0.5rem;
}

.preview-container img {
  max-width: 100%;
  max-height: 160px;
  border-radius: 8px;
  border: 1px solid #ccc;
}

.preview-container span {
  font-size: 0.95rem;
  color: #666;
}

.alert-info {
  background: #ecf6fc;
  color: #2980b9;
  padding: 0.8rem;
  border-radius: 8px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #b3d4ef;
}

.modal-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.cancel-btn {
  background: #bdc3c7;
  color: #2c3e50;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  cursor: pointer;
}

.phone-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.prefix {
  background: #ecf0f1;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  font-size: 1rem;
  border: 1px solid #ccc;
  color: #2c3e50;
}

.phone-input-wrapper input {
  flex: 1;
}

.save-btn {
  background: #27ae60;
  color: white;
  border: none;
  padding: 0.6rem 1.5rem;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
}

.cancel-btn:hover {
  background: #95a5a6;
}

.save-btn:hover {
  background: #2ecc71;
}
@media (max-width: 430px) {
  .modal-content {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    padding: 1rem;
    border-radius: 0;
    overflow-y: auto;
    box-sizing: border-box;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .full {
    grid-column: span 1;
  }

  input,
  select {
    font-size: 1rem;
    width: 100%;
  }

  .phone-input-wrapper {
    flex-direction: column;
    align-items: stretch;
  }

  .prefix {
    padding: 0.5rem;
    width: 100%;
    text-align: center;
    font-size: 1rem;
    margin-bottom: 0.3rem;
  }

  .modal-actions {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }

  .modal-actions button {
    width: 100%;
    padding: 0.8rem;
    font-size: 1rem;
  }

  .alert-info {
    font-size: 0.9rem;
    padding: 0.7rem;
  }

  .preview-container img {
    max-height: 120px;
    width: 100%;
    object-fit: contain;
  }
}

</style>
